import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subscription, from, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, take, tap } from 'rxjs/operators';
import { LessonCarouselService } from '../../home/lesson-carousel/lesson-carousel.service';
import {
  isTakeUnitQuizLocked,
  resolveUnitQuizCooldownHours,
} from '../../home/mini-lesson-section/take-unit-quiz/take-unit-quiz-lock.helpers';
import { ReferralPageBannerService } from '../../home/referral-page-banner/referral-page-banner.service';
import { ActivityPlayerEvent } from '../../shared/activity-player/activity-player-event.enum';
import { ImmersionCourseService } from '../../shared/immersion-course.service';
import { LessonPlayerService } from '../../shared/lesson-player.service';
import { LevelProgressService } from '../../shared/level-progress/level-progress.service';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { OeAppService } from '../../shared/oe-app.service';
import { ProfileLiteExtService } from '../../shared/profile-lite-ext.service';
import { ProfileServiceConfigurationService } from '../../shared/profile-service-configuration.service';
import { LevelProgressDto } from '../../shared/types/LevelProgressDto';
import { NextLessonButtonType } from '../../shared/types/NextButtonActionType';
import { StudyBundleCourseDto } from '../../shared/types/StudyBundleCourseDto';
import { LessonType } from '../../shared/types/lesson-type';
import { LogService } from '../../shared/log.service';
import { WindowService } from '../../shared/window.service';
import { MidLevelExamModalComponent } from '../mid-level-exam-modal/mid-level-exam-modal.component';
import { BreadcrumbTitle, getBreadcrumbTitle } from './lesson-breadcrumb-utils';
import { LessonPageParams } from './lesson-page-params';
import { PlayerViewerType } from '../../shared/types/player-viewer-type.enum';
import { ChatBotStateService } from '../../shared/chat-bot-state.service';
import { ChatBotTypes } from '../../shared/types/chat-bot-types.enum';
import { OneSignalService } from '../../shared/one-signal.service';
import { AnalyticsOutcomeEvents } from '../../shared/types/analytics-outcome.types';
import { OneSignalTags } from '../../shared/types/one-signal-tags';

@Component({
  selector: 'lp2-lesson-page',
  templateUrl: './lesson-page.component.html',
  styleUrls: ['./lesson-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LessonPageComponent implements OnInit, OnDestroy {
  activityUrl$ = new BehaviorSubject<SafeUrl>(null);
  skipQuizFinalizeError$ = new BehaviorSubject<boolean>(false);
  course: StudyBundleCourseDto;

  private userProgress: LevelProgressDto;
  private subscriptions = new Subscription();
  private params: LessonPageParams;
  private lessonNumber: number;
  private lessons: Array<StudyBundleCourseDto>;
  private lessonInfoRequested$ = new BehaviorSubject(false);
  private finishingSkippedUnitQuiz = false;
  public copilotEnabled: boolean;
  public chatBotState$ = this.chatBotStateService.state$;
  public chatBotTypes = ChatBotTypes;

  @ViewChild('iframe') iframe: ElementRef;

  constructor(
    private lessonPlayerService: LessonPlayerService,
    private lessonCarouselService: LessonCarouselService,
    private immersionCourseService: ImmersionCourseService,
    private oeApp: OeAppService,
    private oeAppPersonService: OeAppPersonService,
    private windowService: WindowService,
    private levelProgressService: LevelProgressService,
    private modalService: NgbModal,
    private referralBannerService: ReferralPageBannerService,
    private readonly chatBotStateService: ChatBotStateService,
    private readonly oneSignalService: OneSignalService,
    private readonly profileLiteExtService: ProfileLiteExtService,
    private readonly profileServiceConfigurationService: ProfileServiceConfigurationService
  ) {}

  ngOnInit() {
    this.copilotEnabled = this.oeAppPersonService.copilotEnabled;
    this.params = new LessonPageParams(this.windowService.getSearchParams().get('q'));

    this.subscriptions.add(
      this.getLessonDataWithBookmark(
        this.params.courseId,
        this.params.unitId,
        this.params.lessonType
      ).subscribe((course) => {
        if (!course || !course.mediaData) {
          return this.windowService.redirectToUrl('/');
        }

        const { mediaData: lessonId, bookmark, id: courseId, lessonType } = course;
        this.setActivityUrl(lessonId, bookmark, courseId, lessonType);
        this.course = course;

        this.subscriptions.add(
          this.lessonCarouselService.getLessons(this.params.unitId).subscribe((data) => {
            if (data) {
              this.lessonNumber = data.findIndex((lesson) => lesson.id === course.id) + 1;
              this.lessons = data;
            }
            this.subscriptions.add(
              this.lessonInfoRequested$.subscribe((shouldSendLessonInfo) => {
                if (shouldSendLessonInfo) {
                  this.sendLessonInfo();
                }
              })
            );
          })
        );
      })
    );

    this.subscriptions.add(
      this.levelProgressService.levelProgress$.subscribe(
        (userProgress: LevelProgressDto) => (this.userProgress = userProgress)
      )
    );
    this.levelProgressService.updateLevelProgress();

    this.windowService.nativeWindow().addEventListener('message', this.handlePlayerEvents.bind(this));

    // Uncomment to complete the lesson and go back to home automatically
    // setTimeout(() => {
    //   console.log('this.course', this.course)
    //   window.parent.postMessage({type: 'lessonComplete'}, '*');
    //   setTimeout(()=> window.location.href = 'http://localhost:8082', 500);
    // }, 1000);
  }

  ngOnDestroy() {
    this.windowService.nativeWindow().removeEventListener('message', this.handlePlayerEvents);
    this.subscriptions.unsubscribe();
  }

  private getLessonData(courseId?: number, unitId?: number, lessonType?: StudyBundleCourseDto['lessonType']) {
    if (courseId) {
      return this.lessonCarouselService.getMiniLesson({ courseId, unitId });
    }

    return this.lessonCarouselService.getCurrentMiniLesson(lessonType);
  }

  closeChatBotModal() {
    this.chatBotStateService.closeChatBotMiniLessonModal();
  }

  private getLessonDataWithBookmark(
    courseId?: number,
    unitId?: number,
    lessonType?: StudyBundleCourseDto['lessonType']
  ): Observable<(StudyBundleCourseDto & { bookmark: number }) | null> {
    return this.getLessonData(courseId, unitId, lessonType).pipe(
      mergeMap((course) => {
        if (!course) {
          return of(null);
        }

        if (this.params.isSkippingUnit) {
          return this.canTakeUnitQuiz$(course).pipe(
            map((allowed) => (allowed ? Object.assign({}, course, { bookmark: 0 }) : null))
          );
        }

        return this.immersionCourseService
          .retrieveBookmark(course.id)
          .pipe(map((bookmark) => Object.assign({}, course, { bookmark })));
      })
    );
  }

  // UI guard only; the server must also enforce the flag and the lock
  private canTakeUnitQuiz$(course: StudyBundleCourseDto): Observable<boolean> {
    if (course.type !== LessonType.UNIT_ASSESSMENT) {
      return of(false);
    }

    return this.profileLiteExtService.getPersonData$().pipe(
      take(1),
      mergeMap((personData) =>
        personData && personData.program && personData.program.takeUnitQuizEnabled
          ? this.isTakeUnitQuizLocked$(course.id).pipe(map((locked) => !locked))
          : of(false)
      ),
      catchError((error: HttpErrorResponse) => {
        LogService.error('Take Unit Quiz access check failed', error && error.status);
        return of(false);
      })
    );
  }

  private isTakeUnitQuizLocked$(courseId: number): Observable<boolean> {
    return this.profileServiceConfigurationService.unitQuizCooldownPeriodHours$().pipe(
      take(1),
      mergeMap((hours) =>
        this.lessonCarouselService
          .getLastAssessment(courseId)
          .pipe(map((lastAssessment) => isTakeUnitQuizLocked(lastAssessment, resolveUnitQuizCooldownHours(hours))))
      )
    );
  }

  private setActivityUrl(
    lessonId: string,
    bookmark: number,
    courseId: number,
    lessonType: StudyBundleCourseDto['lessonType']
  ) {
    this.referralBannerService.isElligible$.subscribe((isElligible) => {
      if (isElligible !== undefined) {
        this.lessonPlayerService
          .getActivityUrl$(
            lessonId,
            PlayerViewerType.LESSON,
            isElligible,
            bookmark,
            lessonType,
            courseId,
            this.params.isSkippingUnit
          )
          .subscribe((activityUrl) => {
            this.activityUrl$.next(activityUrl);
          });
      }
    });
  }

  updateBookmark(contentId: number, slideIndex: number) {
    this.immersionCourseService.saveBookmark(contentId, slideIndex).subscribe();
  }

  postMessage(payload) {
    this.iframe.nativeElement.contentWindow.postMessage(payload, '*');
  }

  private getUnitName(userProgress: LevelProgressDto): string {
    return (
      (this.params.unitName && this.params.unitName.toUpperCase()) ||
      `UNIT ${
        userProgress && userProgress.unitTaken ? Math.min(userProgress.unitRequired, userProgress.unitTaken + 1) : 1
      }`
    );
  }

  private getLevelName(userProgress: LevelProgressDto): string {
    return (
      (this.params.levelName && this.params.levelName.toUpperCase()) ||
      `LEVEL ${(userProgress && userProgress.levelNumber) || 0}`
    );
  }

  sendLessonInfo() {
    const levelName = this.getLevelName(this.userProgress);
    const unitName = this.getUnitName(this.userProgress);
    const breadcrumb: BreadcrumbTitle = getBreadcrumbTitle(this.course.title, this.lessonNumber, levelName, unitName);
    this.postMessage({
      type: ActivityPlayerEvent.SEND_LESSON_INFO,
      value: {
        id: this.course.id,
        projectId: this.course.mediaData.split('/').shift(),
        lessonId: this.course.mediaData.split('/').pop(),
        description: this.course.description,
        ...breadcrumb,
        nextLessonButtonType: this.defineNextLessonButtonType(),
        type: this.course.type,
      },
    });
  }

  defineNextLessonButtonType() {
    if (this.params.isFromUnitIndex) {
      return NextLessonButtonType.UNIT_INDEX;
    }
    if (!this.lessons) {
      return null;
    }
    const lessonIndex = this.lessons.findIndex((l) => l.id === this.course.id);
    if (lessonIndex < this.lessons.length - 1) {
      return NextLessonButtonType.NEXT_LESSON;
    }
    if (!this.userProgress || this.userProgress.unitTaken + 1 >= this.userProgress.unitRequired) {
      return null;
    }
    return NextLessonButtonType.NEXT_UNIT;
  }

  sendStudentInfo() {
    const person = this.oeAppPersonService.getPersonData();
    this.postMessage({
      type: ActivityPlayerEvent.SEND_STUDENT_INFO,
      value: {
        id: person.id,
        name: `${person.firstName} ${person.lastName}`,
        image: `${this.windowService.getDomain()}/profile/image/123/123`,
        contactId: person.contactId,
      },
    });
  }

  handlePlayerEvents({ data, origin }) {
    const isWebLessonPlayer = this.isWebLessonPlayerOrigin(origin);
    if (origin !== this.oeApp.activityPlayerHost() && origin !== 'http://localhost:8081' && !isWebLessonPlayer) {
      return;
    }

    if (!data || !data.type) {
      return;
    }

    let eventData = data;
    if (isWebLessonPlayer) {
      eventData = data.data || {};
    }

    this.handleEvent(data.type, eventData.index, eventData.action);
  }

  private isWebLessonPlayerOrigin(origin: string): boolean {
    if (typeof origin !== 'string' || origin === 'null') {
      return false;
    }

    try {
      const playerOrigin = new URL(this.oeApp.webLessonPlayerPageHost).origin;
      return playerOrigin !== 'null' && playerOrigin === origin;
    } catch (e) {
      return false;
    }
  }

  private get isSkippingUnitQuiz(): boolean {
    return !!this.params.isSkippingUnit && !!this.course && this.course.type === LessonType.UNIT_ASSESSMENT;
  }

  retryFinishSkippedUnitQuiz() {
    this.finishSkippedUnitQuiz();
  }

  handleEvent(type: ActivityPlayerEvent, index: number, action: string) {
    switch (type) {
      case ActivityPlayerEvent.ACTIVITY_COMPLETE:
        if (!this.params.isSkippingUnit) {
          this.updateBookmark(this.course.id, index + 1);
        }
        if (index === 0) {
          this.lessonCarouselService.initCourse(this.course.id).subscribe();
        }
        break;
      case ActivityPlayerEvent.REQUEST_LESSON_INFO:
        this.lessonInfoRequested$.next(true);
        break;
      case ActivityPlayerEvent.REQUEST_STUDENT_INFO:
        this.sendStudentInfo();
        break;
      case ActivityPlayerEvent.REFERRAL_BUTTON_CLICKED:
        this.referralBannerService.redirectToReferral();
        break;
      case ActivityPlayerEvent.LESSON_COMPLETE:
        if (this.isSkippingUnitQuiz) {
          break;
        }

        this.lessonCarouselService.completeLesson(this.course.id).subscribe(() => {
          this.oneSignalService.trackOutcome(AnalyticsOutcomeEvents.LAST_LESSON_COMPLETED);
          this.oneSignalService.setTag(OneSignalTags.LAST_LESSON_COMPLETED_TIMESTAMP, Date.now().toString());

          if (this.course.type === LessonType.LESSON) {
            this.oneSignalService.setTag(OneSignalTags.LAST_LESSON_COMPLETED_NAME, this.course.title);
          }

          if (this.course.type === LessonType.UNIT_ASSESSMENT) {
            this.oneSignalService.setTag(OneSignalTags.LAST_LESSON_COMPLETED_UNIT, this.course.title);
            this.lessonCarouselService.finishAssessment(this.course.id).subscribe();
          }
        });
        break;
      case ActivityPlayerEvent.FINISH_LESSON:
        if (this.isSkippingUnitQuiz) {
          this.finishSkippedUnitQuiz();
          break;
        }

        let defaultUrl: string;

        switch (action) {
          case NextLessonButtonType.UNIT_INDEX:
            defaultUrl = '/learning/unit-index.html';
            break;
          default:
            defaultUrl = action === 'next_lesson' ? this.windowService.getLocationHrefWithoutQueries() : '/';
            break;
        }

        if (this.course.type === LessonType.UNIT_ASSESSMENT) {
          this.redirectAfterUnitAssessment(defaultUrl);
        } else {
          this.windowService.redirectToUrl(defaultUrl);
        }
        break;
      case ActivityPlayerEvent.NAVIGATE_HOME:
        this.windowService.redirectToUrl('/');
    }
  }

  // Single finalize point for skip sessions; their LESSON_COMPLETE is ignored
  private finishSkippedUnitQuiz() {
    if (this.finishingSkippedUnitQuiz) {
      return;
    }

    this.finishingSkippedUnitQuiz = true;
    this.skipQuizFinalizeError$.next(false);
    this.subscriptions.add(
      this.lessonCarouselService
        .initCourse(this.course.id)
        .pipe(
          // Finalize needs the unit progression that init creates, and skip sessions may never send ACTIVITY_COMPLETE 0
          mergeMap(() => this.lessonCarouselService.completeLesson(this.course.id)),
          tap(() => this.trackSkippedUnitQuizCompleted()),
          mergeMap(() => this.shouldGoToMidLevelTest$()),
          finalize(() => (this.finishingSkippedUnitQuiz = false))
        )
        .subscribe(
          (goToMidLevelTest) =>
            this.windowService.redirectToUrl(
              goToMidLevelTest ? this.midLevelExamUrl() : this.windowService.getLocationHrefWithoutQueries()
            ),
          (error: HttpErrorResponse) => {
            LogService.error('Skip quiz finalize failed', error && error.status);
            this.skipQuizFinalizeError$.next(true);
          }
        )
    );
  }

  private trackSkippedUnitQuizCompleted() {
    this.oneSignalService.trackOutcome(AnalyticsOutcomeEvents.LAST_LESSON_COMPLETED);
    this.oneSignalService.setTag(OneSignalTags.LAST_LESSON_COMPLETED_TIMESTAMP, Date.now().toString());
    this.oneSignalService.setTag(OneSignalTags.LAST_LESSON_COMPLETED_UNIT, this.course.title);
  }

  private redirectAfterUnitAssessment(defaultUrl: string) {
    this.subscriptions.add(
      this.shouldGoToMidLevelTest$().subscribe((goToMidLevelTest) =>
        this.windowService.redirectToUrl(goToMidLevelTest ? this.midLevelExamUrl() : defaultUrl)
      )
    );
  }

  private midLevelExamUrl(): string {
    return `/mid-level-exam.html?levelId=${this.oeAppPersonService.getPersonData().levelId}`;
  }

  // A failed check only skips the optional mid-level prompt
  private shouldGoToMidLevelTest$(): Observable<boolean> {
    return this.levelProgressService.getShowMidLevelTest().pipe(
      take(1),
      catchError((error: HttpErrorResponse) => {
        LogService.error('Mid-level test check failed', error && error.status);
        return of(false);
      }),
      mergeMap((showMidLevelTest) => (showMidLevelTest ? from(this.askIfWantToTakeMidLevelTest()) : of(false)))
    );
  }

  private async askIfWantToTakeMidLevelTest(): Promise<any> {
    const modalRef = this.modalService.open(MidLevelExamModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });

    return modalRef.result;
  }
}



// WEBPACK FOOTER //
// ./ts/app/learning/lesson-page/lesson-page.component.ts